<template>
  <div>
    <template v-if="commercialAccount">
      <BundleSelection
        :title="bundle.title"
        :description="bundle.description"
        :buttons="bundle.buttons"
      />
    </template>

    <template v-else-if="isBasic || isInTrial">
      <PaywallForBasicUsers
        @scroll-bottom="$emit('scroll-bottom')"
        @checkout="startBasicUserCheckout"
      />
    </template>

    <template v-else>
      <PaywallForPaidUsers
        @scroll-bottom="$emit('scroll-bottom')"
        @email-contact-owner="onEmailAccountOwner"
        @message-account-owner="onMessageAccountOwner"
      />
    </template>

    <Chargebee />
  </div>
</template>

<script>
import { COLORS } from '@/helpers/colors';
import { mapGetters, mapState, mapActions } from 'vuex';
import PaywallForBasicUsers from './PaywallForBasicUsers.vue';
import PaywallForPaidUsers from './PaywallForPaidUsers.vue';
import BundleSelection from './BundleSelection.vue';
import Chargebee from './Chargebee.vue';
import purgeResource from '@/api/payments';

const SUPPORT_EMAIL = 'support@grainfox.ca';

export default {
  name: 'Paywall',
  components: {
    PaywallForBasicUsers,
    BundleSelection,
    PaywallForPaidUsers,
    Chargebee,
  },

  data() {
    return {
      COLORS,
      supportEmail: SUPPORT_EMAIL,
      success: false,
      buyLink: '',
    };
  },

  created() {
    this.$emit('paywall-shown', true);
    this.$nextTick(() => this.$emit('scroll-bottom'));
    const baseUrl = window.location;
    this.buyLink = `${baseUrl.protocol}//${baseUrl.host}/chatbot/sinoa-selection/`;
  },

  computed: {
    ...mapState('user', ['fullName', 'email', 'isExternalUser']),
    ...mapGetters('chargebee', ['isBasic', 'isCommercialBasic', 'isCommercial', 'isInTrial']),
    ...mapState('chatbot', ['subscriptionCart']),
    ...mapState('subscription', ['province', 'ownerEmail', 'ownerPhone', 'ownerName']),
    ...mapState('app', ['isIOS']),
    ...mapGetters('permissions', ['isOwner']),

    commercialAccount() {
      return this.isCommercial || this.isCommercialBasic;
    },

    bundle() {
      if (this.isOwner) {
        return {
          title: 'Want to buy more Questions?',
          description:
            `For Commercial accounts, Question bundles are purchased per user. 
            You can buy Questions for a select few or for everyone on your account.<br>
            To add more Questions to your account, please contact GrainFox Support. 
            Click below and we’ll help you get set up quickly.`,
          buttons: [{
            buttonText: 'Contact GrainFox Support',
            action: this.onContactSupport,
            color: COLORS.grainfoxAIButton,
          }],
        };
      }

      return {
        title: 'Out of Questions?',
        description:
            'Ask your account owner to add more Sinoa Questions so you can keep using Sinoa',
        buttons: [{
          buttonText: 'Email Account Owner',
          action: () => this.onEmailAccountOwner(),
          color: this.COLORS.grainfoxAIButton,
        },
        ...(this.ownerPhone ? [{
          buttonText: 'Message Account Owner',
          action: () => this.onMessageAccountOwner(),
          color: this.COLORS.primary,
        }] : []),
        ],
      };
    },

    emailSubject() {
      return 'Request for More Sinoa Questions';
    },

    emailBody() {
      const greeting = this.ownerName ? `Hi ${this.ownerName},` : 'Hi,';
      const footer = this.email ? `\n\nThank you,\n${this.email}` : '';
      const linkLine = this.buyLink && !this.commercialAccount ? `\n\nYou can buy more questions here: ${this.buyLink}` : '';
      return `${greeting}\n\nI’m out of Sinoa Questions in GrainFox.\n\nCan you please add more to our account so I can keep using the AI chat?${linkLine}${footer}`;
    },

    smsBody() {
      const name = this.ownerName || 'there';
      const linkPart = this.buyLink && !this.commercialAccount ? ` You can buy more questions here: ${this.buyLink}` : '';
      return `Hi ${name}, I’m out of Sinoa Questions in GrainFox. Can you please add more to our account so I can keep using the AI chat?${linkPart}`;
    },
  },

  methods: {
    ...mapActions('subscription', ['patchSubscription']),
    ...mapActions('chatbot', ['applyBundleCredit']),

    onEmailAccountOwner() {
      if (!this.ownerEmail) return;

      const mailto = `mailto:${encodeURIComponent(this.ownerEmail)}`
        + `?subject=${encodeURIComponent(this.emailSubject)}`
        + `&body=${encodeURIComponent(this.emailBody)}`;

      const opened = window.open(mailto, '_self');
      if (!opened) window.location.href = mailto;
    },

    onMessageAccountOwner() {
      if (!this.ownerPhone) return;

      const phone = this.ownerPhone.replace(/[^\d+]/g, '');
      const sep = this.isIOS === true ? '&' : '?';
      const smsUrl = `sms:${encodeURIComponent(phone)}${sep}body=${encodeURIComponent(this.smsBody)}`;

      try {
        const opened = window.open(smsUrl, '_self');
        if (!opened) {
          window.location.href = smsUrl;
        }
      } catch (e) {
        navigator.clipboard?.writeText(this.smsBody).catch(() => {});

        const mailto = `mailto:${encodeURIComponent(this.ownerEmail || '')}`
      + `?subject=${encodeURIComponent(this.emailSubject)}`
      + `&body=${encodeURIComponent(this.emailBody)}`;
        window.location.href = mailto;
      }
    },

    buildSubject() {
      return this.isExternalUser ? 'Request for More Sinoa Questions: ARC Commercial Account'
        : 'Request for More Sinoa Questions – Commercial Account';
    },

    buildBody() {
      const lines = [
        'Hi GrainFox Support,',
        "I'm a Commercial user and would like to purchase additional AI Questions for my account. Please confirm pricing and next steps for adding them.",
        'Thanks,',
        `${this.fullName}`,
        `${this.email}`,
      ];
      return lines.filter(Boolean).join('\n\n').trim();
    },

    onContactSupport() {
      const subject = encodeURIComponent(this.buildSubject());
      const body = encodeURIComponent(this.buildBody());
      const mailtoLink = `mailto:${this.supportEmail}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
    },

    async onSuccess() {
      try {
        if (this.province?.id) {
          await this.patchSubscription({ province: this.province.id, country: this.province.country_id });
        }
        await purgeResource(['subscription', 'estimate']);

        const internalName = this.subscriptionCart?.selectedSinoa ?? null;
        if (internalName) {
          await this.applyBundleCredit({
            internalName,
            autoRenew: this.subscriptionCart?.autoRenew ?? false,
          });
        }
        this.$snackbar.success(
          'Plan and Bundle purchased successfully. We’re updating your account…',
        );
      } catch (error) {
        this.$snackbar.error(error?.message || 'Something went wrong while updating your plan.');
      } finally {
        window.location.replace('/dashboard/?openSinoa=true');
      }
    },

    async startBasicUserCheckout() {
      if (!this.$chargebee?.isReady) {
        this.$snackbar.error('Payments are initializing. Please try again.');
        return;
      }

      const selectedPlan = this.subscriptionCart?.selectedPlan || null;
      const selectedAddons = Array.isArray(this.subscriptionCart?.selectedAddons)
        ? this.subscriptionCart.selectedAddons
        : [];
      const selectedSinoa = this.subscriptionCart?.selectedSinoa || null;

      try {
        await this.$chargebee.openSubscriptionCheckout({
          subscriptionCart: {
            selectedPlan,
            selectedAddons,
            selectedSinoa,
          },
          callbacks: {
            close: () => {},
            loaded: () => {},
            success: this.onSuccess,
            error: (err) => {
              this.$snackbar.error(err?.message || err || 'Checkout failed.');
            },
          },
        });
      } catch (e) {
        this.$snackbar.error('Failed to open checkout. Please try again.');
      }
    },
  },
};
</script>
